import React, { useState } from "react";
import {
  AppBar,
  Toolbar,
  IconButton,
  Box,
  Avatar,
  Typography,
  Divider,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
} from "@mui/material";
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNone';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SearchIcon from '@mui/icons-material/Search';
import GroupIcon from '@mui/icons-material/Group';
import EmailIcon from '@mui/icons-material/Email';
import ArchiveIcon from '@mui/icons-material/Archive';
import SettingsIcon from '@mui/icons-material/Settings';
import { useNavigate, useLocation } from "react-router-dom";
import LogoutIcon from '@mui/icons-material/Logout';
import { supabase } from "../supabase"; 

export default function AdminHeader({ user }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);

  const handleNavigation = (path) => {
    navigate(path);
    setDrawerOpen(false);
  };

  const isCurrentPath = (path) => {
    return location.pathname === path;
  };

  const menuItems = [
    {
      text: "Discovery",
      icon: <SearchIcon />,
      path: "/dashboard",
      showDivider: false
    },
    {
      text: "Outreach Flow",
      icon: <EmailIcon />,
      path: "/outreach",
      showDivider: false
    },
    {
      text: "Archive",
      icon: <ArchiveIcon />,
      path: "/archive",
      showDivider: true
    },
    {
      text: "User Accounts",
      icon: <GroupIcon />,
      path: "/create-user",
      showDivider: false,
      adminOnly: true
    },
    {
      text: "Settings",
      icon: <SettingsIcon />,
      path: "/settings",
      showDivider: true,
      adminOnly: true
    }
  ];

  return (
    <>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          backgroundColor: "#f5f5f5",
          color: "#000",
          zIndex: 1201,
        }}
      >
        <Toolbar sx={{ display: "flex", justifyContent: "space-between", minHeight: 64, px: { xs: 1, md: 2 } }}>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <img
              src="/logo/Maniko_logo.webp"
              alt="Logo"
              onClick={() => navigate("/dashboard")}
              style={{ height: 28, cursor: "pointer" }}
            />
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 0, md: 2} }}>
            <IconButton>
              <NotificationsNoneOutlinedIcon />
            </IconButton>

            <Box sx={{ display: "flex", alignItems: "center", gap: 1, lineHeight: 'normal' }}>
              <Avatar
                sx={{
                  width: 32,
                  height: 32,
                  bgcolor: "#ddd",
                  color: "#888",
                  fontSize: "0.875rem",
                  textTransform: 'uppercase',
                }}
              >
                {user?.name?.charAt(0) || "?"}
              </Avatar>
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>{user?.name}</Typography>
                <Typography variant="caption" sx={{ color: "#888" }}>{user?.role} account</Typography>
              </Box>
            </Box>

            <IconButton onClick={() => setDrawerOpen(prev => !prev)} size="small">
              <ExpandMoreIcon
                sx={{
                  transform: drawerOpen && 'rotate(90deg)',
                  transition: 'transform 0.3s ease',
                }}
              />
            </IconButton>
          </Box>
        </Toolbar>

        <Box
          component="hr"
          sx={{
            border: 0,
            height: '1px',
            backgroundColor: '#d0d0d0',
            width: '100%',
            margin: 0,
          }}
        />
      </AppBar>

      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{ sx: { width: 240, backgroundColor: "#f9f9f9" } }}
      >
        <Box sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              Menu
            </Typography>
            <List>
              {menuItems.map((item) => (
                item.adminOnly && user?.role !== 'admin' ? null : (
                  <React.Fragment key={item.text}>
                    <ListItem disablePadding>
                      <ListItemButton 
                        onClick={() => handleNavigation(item.path)}
                        sx={{
                          backgroundColor: isCurrentPath(item.path) ? 'rgba(0, 0, 0, 0.04)' : 'transparent',
                          '&:hover': {
                            backgroundColor: isCurrentPath(item.path) ? 'rgba(0, 0, 0, 0.08)' : 'rgba(0, 0, 0, 0.04)',
                          }
                        }}
                      >
                        <ListItemIcon>
                          {item.icon}
                        </ListItemIcon>
                        <ListItemText primary={item.text} />
                      </ListItemButton>
                    </ListItem>
                    {item.showDivider && <Divider sx={{ my: 1 }} />}
                  </React.Fragment>
                )
              ))}
            </List>
          </Box>

          {/* Logout at bottom */}
          <Box sx={{ pb: 2 }}>
            <Divider sx={{ mb: 1 }} />
            <ListItem disablePadding>
              <ListItemButton
                onClick={async () => {
                  await supabase.auth.signOut();
                  navigate("/");
                }}
              >
                <ListItemIcon>
                  <LogoutIcon sx={{ color: '#d32f2f' }} />
                </ListItemIcon>
                <ListItemText primary="Logout" primaryTypographyProps={{ color: '#d32f2f', fontWeight: 500 }} />
              </ListItemButton>
            </ListItem>
          </Box>
        </Box>
      </Drawer>
    </>
  );
}
